import React, { useState } from "react";
import {
  Trash2,
  CheckCircle,
  Clock,
  Edit2,
  X,
  Image as ImageIcon,
  History,
  Mail,
} from "lucide-react";
import {
  formatDurationDisplay,
  getLeaveTypeColor,
} from "../../../utils/leaveHelpers";
import { formatDate, formatTime } from "../../../utils/timeHelpers";
import { ROLES } from "../../../config/constants";

export default function LeaveList({
  leaves,
  role,
  user,
  onDelete,
  onSign,
  onEdit,
  onNotify,
  proxyOptions = [],
  proxyEmails = {},
}) {
  const [viewImage, setViewImage] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const handleNotify = async (leave) => {
    if (sendingId) return;
    setSendingId(leave.id);
    try {
      const event = leave.status === "pending" && !leave.approvalCancelled
        ? (leave.proxySignature ? "proxy" : "submitted")
        : "decision";
      await onNotify(leave.id, event);
    } finally {
      setSendingId(null);
    }
  };

  const canModify = (leave) => {
    if (role === ROLES.ADMIN) return true;
    if (leave.status === "approved") return false;
    return user.uid === leave.uid;
  };

  const now = new Date();
  // 已過期 = 結束時間已過 **且** 已核准；其餘留在進行中區
  const activeLeaves = leaves
    .filter((l) => new Date(l.endDate) >= now || l.status !== "approved")
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  const expiredLeaves = leaves
    .filter((l) => new Date(l.endDate) < now && l.status === "approved")
    .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

  const LeaveCard = ({ leave }) => (
    <div
      key={leave.id}
      className={`paper-item leave-item bg-white px-3 py-2 rounded-xl shadow-sm border border-slate-100 relative hover:shadow-md transition-all group ${
        new Date(leave.endDate) < now ? "opacity-75 grayscale-[30%]" : ""
      }`}
    >
      {/* 第一行 */}
      <div className="flex justify-between items-center mb-1">
        <div className="flex items-center gap-2 overflow-hidden">
          <span
            className={`text-[10px] font-bold px-2 py-0.5 rounded text-white ${getLeaveTypeColor(
              leave.type
            )}`}
          >
            {leave.typeName}
          </span>

          <span className="font-bold text-slate-700 text-sm truncate">
            {leave.userName}
          </span>

          <div
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              leave.status === "approved" ? "bg-green-400" : "bg-orange-300"
            }`}
            title={leave.status === "approved" ? "已核准" : "待簽核"}
          ></div>

          {leave.proofImage && (
            <div
              className="ml-1 w-8 h-6 rounded border border-slate-200 bg-slate-100 cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all flex-shrink-0 overflow-hidden relative"
              onClick={() => setViewImage(leave.proofImage)}
            >
              <img
                src={leave.proofImage}
                alt="證"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 right-0 bg-black/50 p-[1px] rounded-tl-sm">
                <ImageIcon size={6} className="text-white" />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-slate-500 bg-slate-50 px-1.5 rounded">
            {formatDurationDisplay(leave.days, leave.hours)}
          </span>

          <div className="flex items-center border-l border-slate-200 pl-2 ml-1 gap-1.5">
            {(role === ROLES.ADMIN ||
              (leave.status === "pending" && !leave.approvalCancelled &&
                (leave.proxySignature
                  ? ((leave.proxySignerUid || leave.proxyUid) ? (leave.proxySignerUid || leave.proxyUid) === user.uid : leave.proxyEmail?.toLowerCase() === user.email?.toLowerCase())
                  : leave.uid === user.uid))) && (
            <button
              onClick={() => handleNotify(leave)}
              disabled={Boolean(sendingId)}
              className="flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-2.5 py-1.5 rounded-lg shadow-sm transition-colors"
              title="重試寄送通知信"
            >
              <Mail size={14} /> {sendingId === leave.id ? "寄送中" : "通知信"}
            </button>
            )}

            {canModify(leave) && (
              <>
                <button
                  onClick={() => onEdit(leave)}
                  aria-label="編輯假單" title="編輯假單"
                  className="text-slate-400 hover:text-blue-500 p-1.5 hover:bg-blue-50 rounded transition-colors"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => onDelete(leave.id)}
                  aria-label="刪除假單" title="刪除假單"
                  className="text-slate-400 hover:text-red-500 p-1.5 hover:bg-red-50 rounded transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 第二行 */}
      <div className="flex items-center gap-1.5 text-xs text-slate-600 mb-1">
        <Clock size={13} className="mt-0.5 text-slate-500 shrink-0" />
        <span className="font-mono font-bold truncate">
          {formatDate(new Date(leave.startDate))}{" "}
          {formatTime(new Date(leave.startDate)).slice(0, 5)}
          <span className="mx-1 text-slate-400">➜</span>
          {formatDate(new Date(leave.endDate))}{" "}
          {formatTime(new Date(leave.endDate)).slice(0, 5)}
        </span>
      </div>

      {/* 第三行 */}
      {(leave.proxyUid || leave.proxyEmail) && (
        <div className="text-xs text-slate-600 break-words py-1">
          職務代理人：{proxyOptions.find(p => p.uid === leave.proxyUid)?.fullName || proxyOptions.find(p => p.uid === leave.proxyUid)?.name || leave.proxyEmail || "已指定員工"}
          <div className="break-all">通知信箱：{proxyEmails[leave.proxyUid] || leave.proxyEmail || "尚未取得通知信箱"}</div>
        </div>
      )}
      <div className="flex gap-2 items-center border-t border-slate-50 pt-2 mt-1">
        <div className="flex-1 truncate text-slate-700 text-xs font-medium">
          {leave.reason}
        </div>

        <div className="flex items-center gap-2 shrink-0 text-[10px]">
          {leave.proxySignature ? (
            <span className="text-green-600 font-bold bg-green-50 px-1.5 rounded flex items-center gap-0.5">
              <CheckCircle size={8} /> {leave.proxySignature}
            </span>
          ) : (
            <button
              onClick={() => onSign(leave.id, "proxy")}
              className="text-blue-500 hover:underline"
            >
              代簽
            </button>
          )}

          <span className="text-slate-200">|</span>

          {leave.managerSignature ? (
            <div className="flex items-center gap-1">
              <span className="text-green-600 font-bold bg-green-50 px-1.5 rounded flex items-center gap-0.5">
                <CheckCircle size={8} /> {leave.managerSignature}
              </span>
              
              {/* [修正] 加大取消核准按鈕 */}
              {role === ROLES.ADMIN && (
                <button
                  onClick={() => onSign(leave.id, "cancel")}
                  className="bg-slate-100 hover:bg-red-100 text-slate-400 hover:text-red-500 p-2 rounded-full transition-colors ml-1"
                  title="取消核准"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ) : role === ROLES.ADMIN ? (
            <button
              onClick={() => onSign(leave.id, "manager")}
              className="text-blue-500 hover:underline"
            >
              核准
            </button>
          ) : (
            <span className="text-slate-300">待核</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="space-y-2">
        {activeLeaves.map((leave) => (
          <LeaveCard key={leave.id} leave={leave} />
        ))}
      </div>

      {expiredLeaves.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-3 mb-4 px-1 opacity-60">
            <div className="h-[1px] bg-slate-300 flex-1"></div>
            <div className="flex items-center gap-1 text-xs font-bold text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
              <History size={12} /> 歷史紀錄 ({expiredLeaves.length})
            </div>
            <div className="h-[1px] bg-slate-300 flex-1"></div>
          </div>
          <div className="space-y-2">
            {expiredLeaves.map((leave) => (
              <LeaveCard key={leave.id} leave={leave} />
            ))}
          </div>
        </div>
      )}


      {viewImage && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in"
          onClick={() => setViewImage(null)}
        >
          <div
            className="relative max-w-lg w-full bg-white p-2 rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setViewImage(null)}
              className="absolute -top-4 -right-4 bg-white text-slate-800 rounded-full p-2 shadow-lg hover:bg-slate-100 z-10"
            >
              <X size={20} />
            </button>
            <img
              src={viewImage}
              alt="Proof"
              className="w-full h-auto rounded-xl max-h-[80vh] object-contain bg-black"
            />
          </div>
        </div>
      )}
    </>
  );
}
